import { Controller } from '@hotwired/stimulus'
import markVisibleEdges from '../utils/visible_edges'

// Client-side filter for the modifier group's bulk-assignment list. Hides non-matching dishes and
// any menu or section left empty. Bootstrap collapse reveals the field; this only reacts to it.
export default class extends Controller {
  static targets = ['row', 'group', 'section', 'empty', 'search', 'selectAll']

  focusSearch() {
    this.searchTarget.focus()
  }

  // Fired when the filter collapses and whenever the body is (re)loaded, so the list is never left
  // filtered by a box that is closed or that no longer matches what is on screen. The search field
  // lives in the modal header, outside the body being replaced, so its value survives the swap.
  clear() {
    this.searchTarget.value = ''
    this.filter()
  }

  filter() {
    const query = this.searchTarget.value.trim().toLowerCase()

    this.rowTargets.forEach((row) => {
      row.hidden = query !== '' && !row.dataset.name.includes(query)
    })

    // Collapse upwards: a menu with no visible dish goes, then a section with no visible menu.
    this.hideEmpty(this.groupTargets, 'row')
    this.hideEmpty(this.sectionTargets, 'group')

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = this.sectionTargets.some((section) => !section.hidden)
    }

    this.markEdges()
    this.syncMenuToggles()
  }

  hideEmpty(containers, childTarget) {
    const selector = `[data-modifier-usage-target="${childTarget}"]:not([hidden])`
    containers.forEach((container) => {
      container.hidden = !container.querySelector(selector)
    })
  }

  markEdges() {
    this.groupTargets.forEach((group) => markVisibleEdges(this.rowsIn(group)))
  }

  // "All" is rendered from the state at load, so ticking every dish by hand would otherwise leave
  // it unchecked. It tracks the visible rows only, matching what toggleMenu acts on.
  syncMenuToggles() {
    this.selectAllTargets.forEach((toggle) => {
      const group = toggle.closest('[data-modifier-usage-target="group"]')
      if (!group) return

      const boxes = this.rowsIn(group)
        .filter((row) => !row.hidden)
        .map((row) => row.querySelector('input[type="checkbox"]'))

      toggle.checked = boxes.length > 0 && boxes.every((box) => box.checked)
      toggle.disabled = boxes.length === 0
    })
  }

  // A menu's "All" box only touches the rows the current filter leaves visible, so narrowing to
  // "taco" and clicking All can't silently attach the rest of the menu.
  toggleMenu(event) {
    const group = event.target.closest('[data-modifier-usage-target="group"]')
    if (!group) return

    this.rowsIn(group)
      .filter((row) => !row.hidden)
      .forEach((row) => {
        const checkbox = row.querySelector('input[type="checkbox"]')
        if (checkbox) checkbox.checked = event.target.checked
      })
  }

  rowsIn(group) {
    return Array.from(group.querySelectorAll('[data-modifier-usage-target="row"]'))
  }
}
